import { Suspense, useState } from 'react'
import { Button, Spinner } from '@heroui/react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { UserAvatar } from '@/components/ui/avatar'
import { ArrowLeftIcon, LogoutIcon } from '@/components/ui/icons'
import { Logo } from '@/components/ui/logo'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { useAuth, useAuthActions } from '@/features/auth'
import { NotificationBell } from '@/features/notifications'
import { ExcelPreviewHost } from '@/features/reports'
import { paths } from '@/routes/paths'

import { pageIdentityFor } from './page-identity'
import type { PageIdentityOverride, ShellOutletContext } from './page-identity'

/**
 * The centred column and gutters the home screen inherits from `main`.
 *
 * Only home uses it now: every module paints its own full-bleed chrome through
 * `ModuleShell`, which carries the same centred column inside a canvas of its
 * own. It stays exported because home and the standalone screens — the payment
 * return, an invitation — still want a plain column.
 */
export const PAGE_CONTAINER = 'mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10'

export function AppLayout() {
  const { user } = useAuth()
  const { logout } = useAuthActions()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [isSigningOut, setIsSigningOut] = useState(false)
  /** Set by a screen deeper in a module — see `usePageIdentity`. */
  const [override, setOverride] = useState<PageIdentityOverride | null>(null)
  const [outletContext] = useState<ShellOutletContext>(() => ({ setPageIdentity: setOverride }))

  // The logo is the home marker, so it only appears at home. Anywhere else the
  // same slot names the screen you are on — one bar, not a bar plus a page
  // header saying the same thing a row lower.
  const isHome = pathname === paths.dashboard
  const moduleIdentity = pageIdentityFor(pathname)
  const identity =
    moduleIdentity && override
      ? {
          ...moduleIdentity,
          title: override.title ?? moduleIdentity.title,
          subtitle: override.subtitle ?? moduleIdentity.subtitle,
        }
      : moduleIdentity
  const backTo = override?.backTo ?? paths.dashboard

  async function handleLogout() {
    setIsSigningOut(true)
    try {
      await logout()
      navigate(paths.login, { replace: true })
    } finally {
      setIsSigningOut(false)
    }
  }

  return (
    // `brand-scope` re-points HeroUI's accent and focus tokens to the brand
    // purple, so the shell's buttons and focus rings match the sign-in screen.
    // The shell sits on `surface`, not `background`: the design runs one flat sheet
    // from the app bar down, and separates the app tiles with a border instead.
    <div className="brand-scope bg-surface flex min-h-dvh flex-col">
      <header className="bg-surface border-border sticky top-0 z-20 border-b">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-3 px-4 sm:h-[72px] sm:gap-4 sm:px-6">
          {isHome ? (
            <Link
              to={paths.dashboard}
              aria-label="NexQR home"
              className="rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current"
            >
              <Logo variant="brand" className="text-foreground h-7 w-auto sm:h-8" />
            </Link>
          ) : (
            identity && (
              <span className="flex min-w-0 items-center gap-2">
                {/* Icon-only, so it carries its label for screen readers and a
                    44px box for fingers — the glyph inside stays small. The
                    negative start margin puts that glyph, rather than the
                    button box, on the column's edge. */}
                <Button
                  variant="ghost"
                  aria-label={backTo === paths.dashboard ? 'Back to home' : 'Back'}
                  onPress={() => navigate(backTo)}
                  className="text-muted hover:text-foreground hover:bg-background size-11 shrink-0 rounded-full p-0 sm:-ms-3.5"
                >
                  <ArrowLeftIcon width={18} height={18} />
                </Button>

                <span className="bg-brand-100 text-brand-600 flex size-9 shrink-0 items-center justify-center rounded-xl">
                  <identity.icon width={18} height={18} />
                </span>

                <span className="flex min-w-0 flex-col">
                  {/* The bar now carries the page's title, so the heading lives
                      here rather than in the body — a screen with no h1 reads
                      as untitled to a screen reader. */}
                  <h1 className="truncate text-[15px] leading-tight font-semibold tracking-[-0.01em]">
                    {identity.title}
                  </h1>
                  <span className="text-muted truncate text-xs">{identity.subtitle}</span>
                </span>
              </span>
            )
          )}

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <NotificationBell />

            {user && (
              <span className="flex items-center gap-2">
                <UserAvatar name={user.name} photoUrl={'photoUrl' in user ? user.photoUrl : null} />
                {/* The name is the avatar's accessible text, so it stays in the
                    tree at every width even when the layout hides it. */}
                <span className="hidden text-sm font-medium sm:inline">{user.name}</span>
                <span className="sr-only sm:hidden">{user.name}</span>
              </span>
            )}

            <Button
              variant="primary"
              size="sm"
              onPress={handleLogout}
              isDisabled={isSigningOut}
              // The label is dropped below `sm`, where the button is icon-only.
              aria-label="Sign out"
              className="h-9 rounded-full px-4 font-medium"
            >
              {isSigningOut ? <Spinner size="sm" /> : <LogoutIcon width={16} height={16} />}
              <span className="hidden sm:inline">{isSigningOut ? 'Signing out…' : 'Sign Out'}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Full width, no padding: a page that wants edge-to-edge chrome — a
          bar, a coloured canvas — cannot get it from inside a centred column,
          and faking it with 100vw adds a horizontal scrollbar. Each page owns
          its own container and gutters instead; the shared values live in
          PAGE_CONTAINER. */}
      <main className="w-full flex-1">
        {/* Pages load on first visit — see `AppRoutes`. The boundary sits here,
            under the app bar, so moving between modules swaps only the body
            for a spinner rather than blanking the whole shell. */}
        <Suspense
          fallback={
            <div className="flex justify-center py-24" role="status" aria-live="polite">
              <Spinner size="lg" />
              <span className="sr-only">Loading…</span>
            </div>
          }
        >
          <Outlet context={outletContext} />
        </Suspense>
      </main>
      <ExcelPreviewHost />
    </div>
  )
}
